var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router/composables";
import { useConfirmDialog } from "@/composables/confirmDialog";
import { useToast } from "@/composables/toast";
import { useHistoryStore } from "@/stores/historyStore";
import localize from "@/utils/localization";
import { bytesLabelFormatter, bytesValueFormatter } from "./Charts/formatters";
import { fetchAllHistoriesSizeSummary, purgeHistoryById, undeleteHistoryById } from "./service";
import BarChart from "./Charts/BarChart.vue";
import RecoverableItemSizeTooltip from "./RecoverableItemSizeTooltip.vue";
import SelectedItemActions from "./SelectedItemActions.vue";
import Heading from "@/components/Common/Heading.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'HistoriesStorageOverview',
    setup(__props) {
        const historyStore = useHistoryStore();
        const router = useRouter();
        const { success: successToast, error: errorToast } = useToast();
        const { confirm } = useConfirmDialog();
        const historiesSizeSummaryMap = new Map();
        const topTenHistoriesBySizeData = ref(null);
        const activeVsArchivedVsDeletedTotalSizeData = ref(null);
        const isLoading = ref(true);
        const numberOfHistoriesToDisplayOptions = [10, 20, 50];
        const numberOfHistoriesToDisplay = ref(numberOfHistoriesToDisplayOptions[0]);
        onMounted(() => __awaiter(this, void 0, void 0, function* () {
            isLoading.value = true;
            const allHistoriesSizeSummary = yield fetchAllHistoriesSizeSummary();
            allHistoriesSizeSummary.forEach((history) => historiesSizeSummaryMap.set(history.id, history));
            buildGraphsData();
            isLoading.value = false;
        }));
        function buildGraphsData() {
            const allHistoriesSizeSummary = Array.from(historiesSizeSummaryMap.values());
            topTenHistoriesBySizeData.value = buildTopHistoriesBySizeData(allHistoriesSizeSummary);
            activeVsArchivedVsDeletedTotalSizeData.value = buildActiveVsArchivedVsDeletedTotalSizeData(allHistoriesSizeSummary);
        }
        function buildTopHistoriesBySizeData(historiesSizeSummary) {
            const topTenHistoriesBySize = historiesSizeSummary
                .sort((a, b) => b.size - a.size)
                .slice(0, numberOfHistoriesToDisplay.value);
            return topTenHistoriesBySize.map((history) => ({
                id: history.id,
                label: history.name,
                value: history.size,
            }));
        }
        function buildActiveVsArchivedVsDeletedTotalSizeData(historiesSizeSummary) {
            const activeHistoriesSize = historiesSizeSummary
                .filter((history) => !history.deleted && !history.archived)
                .reduce((total, history) => total + history.size, 0);
            const archivedHistoriesSize = historiesSizeSummary
                .filter((history) => history.archived)
                .reduce((total, history) => total + history.size, 0);
            const deletedHistoriesSize = historiesSizeSummary
                .filter((history) => history.deleted)
                .reduce((total, history) => total + history.size, 0);
            return [
                {
                    id: "active",
                    label: "Active",
                    value: activeHistoriesSize,
                },
                {
                    id: "archived",
                    label: "Archived",
                    value: archivedHistoriesSize,
                },
                {
                    id: "deleted",
                    label: "Deleted",
                    value: deletedHistoriesSize,
                },
            ];
        }
        function isRecoverableDataPoint(dataPoint) {
            if (dataPoint) {
                const historiesSizeSummary = historiesSizeSummaryMap.get(dataPoint.id || "");
                return (historiesSizeSummary === null || historiesSizeSummary === void 0 ? void 0 : historiesSizeSummary.deleted) || dataPoint.id === "deleted";
            }
            return false;
        }
        function isArchivedDataPoint(dataPoint) {
            if (dataPoint) {
                const historiesSizeSummary = historiesSizeSummaryMap.get(dataPoint.id);
                return (historiesSizeSummary === null || historiesSizeSummary === void 0 ? void 0 : historiesSizeSummary.archived) || false;
            }
            return false;
        }
        function onSetCurrentHistory(historyId) {
            return __awaiter(this, void 0, void 0, function* () {
                yield historyStore.setCurrentHistory(historyId);
                router.push({ path: "/" });
            });
        }
        function onViewHistory(historyId) {
            router.push({ name: "HistoryOverview", params: { historyId } });
        }
        function onUndeleteHistory(historyId) {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    const result = yield undeleteHistoryById(historyId);
                    const history = historiesSizeSummaryMap.get(historyId);
                    if (history && !result.deleted) {
                        history.deleted = result.deleted;
                        historiesSizeSummaryMap.set(historyId, history);
                        successToast(localize("History undeleted successfully."));
                        buildGraphsData();
                    }
                }
                catch (error) {
                    errorToast(`${error}`, localize("An error occurred while undeleting the history."));
                }
            });
        }
        function onPermanentlyDeleteHistory(historyId) {
            return __awaiter(this, void 0, void 0, function* () {
                const confirmed = yield confirm(localize("Are you sure you want to permanently delete this history? This action cannot be undone."), {
                    title: localize("Permanently delete history?"),
                    okVariant: "danger",
                    okTitle: localize("Permanently delete"),
                    cancelTitle: localize("Cancel"),
                });
                if (!confirmed) {
                    return;
                }
                try {
                    const result = yield purgeHistoryById(historyId);
                    const history = historiesSizeSummaryMap.get(historyId);
                    if (history && result.purged) {
                        historiesSizeSummaryMap.delete(historyId);
                        successToast(localize("History permanently deleted successfully."));
                        buildGraphsData();
                    }
                }
                catch (error) {
                    errorToast(`${error}`, localize("An error occurred while permanently deleting the history."));
                }
            });
        }
        return { __sfc: true, historyStore, router, successToast, errorToast, confirm, historiesSizeSummaryMap, topTenHistoriesBySizeData, activeVsArchivedVsDeletedTotalSizeData, isLoading, numberOfHistoriesToDisplayOptions, numberOfHistoriesToDisplay, buildGraphsData, buildTopHistoriesBySizeData, buildActiveVsArchivedVsDeletedTotalSizeData, isRecoverableDataPoint, isArchivedDataPoint, onSetCurrentHistory, onViewHistory, onUndeleteHistory, onPermanentlyDeleteHistory, localize, bytesLabelFormatter, bytesValueFormatter, BarChart, RecoverableItemSizeTooltip, SelectedItemActions, Heading, LoadingSpan };
    }
});
